import { FunnelIcon } from "@heroicons/react/24/outline";
import React from "react";

type Props = {
  hasFilter: boolean;
  filterValues: string[];
  setOpenModal: (modal: string) => void;
  title: string;
  modalKey: string;
  /** When set and matches modalKey, show count "X Report(s)" instead of each value */
  groupBy?: string;
  /** Label for empty state when groupBy is set (e.g. "Company/Companies") */
  groupByLabel?: string;
  /** When true, filter cannot be opened (e.g. locked until Clear). */
  readOnly?: boolean;
};

const CompletedTableFilter = ({
  hasFilter,
  filterValues,
  setOpenModal,
  title,
  modalKey,
  groupBy,
  groupByLabel,
  readOnly = false,
}: Props) => {
  const isGrouped = !!groupBy && groupBy === modalKey;
  const displayTitle = isGrouped && groupByLabel ? groupByLabel : title;
  const showCount = isGrouped && hasFilter;

  const handleOpenModal = () => {
    if (!readOnly) setOpenModal(modalKey);
  };

  return (
    <div className="min-w-48 shrink-0">
      <div
        role={readOnly ? undefined : "button"}
        tabIndex={readOnly ? undefined : 0}
        onClick={readOnly ? undefined : handleOpenModal}
        onKeyDown={
          readOnly
            ? undefined
            : (e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  handleOpenModal();
                }
              }
        }
        aria-readonly={readOnly || undefined}
        className={`flex flex-row items-center gap-2 justify-between border border-gray-200 rounded-lg p-2 h-full ${
          readOnly
            ? "cursor-default bg-gray-50"
            : "cursor-pointer hover:border-gray-300"
        }`}
      >
        <div className="flex min-w-0 flex-1 flex-row flex-wrap items-center gap-2">
          {!hasFilter && (
            <div className="text-sm text-gray-500">{displayTitle}</div>
          )}
          {hasFilter && !showCount && (
            <>
              {filterValues.map((value) => (
                <div
                  key={value}
                  className="text-sm text-gray-500 border border-gray-200 rounded-lg p-2 bg-gray-100"
                >
                  {value}
                </div>
              ))}
            </>
          )}
          {showCount && (
            <div className="w-full min-w-0 text-sm text-gray-500 border border-gray-200 rounded-lg p-2 bg-gray-100">
              {groupByLabel && groupByLabel.includes("/")
                ? `${filterValues.length} ${
                    filterValues.length !== 1
                      ? groupByLabel.split("/")[1]?.trim() ?? "Reports"
                      : groupByLabel.split("/")[0]?.trim() ?? "Report"
                  }`
                : `${filterValues.length} Report${
                    filterValues.length !== 1 ? "s" : ""
                  }`}
            </div>
          )}
        </div>
        <FunnelIcon className="h-4 w-4 shrink-0" aria-hidden />
      </div>
    </div>
  );
};

export default CompletedTableFilter;
